import React from 'react'

import {t} from '@lib/helpers'
import {html, useCaseIllustrations, zipWithImage} from './constant'

const SubTopic = ({children}) => (
  <div className='PartnerAws__usecase__subtopic'>
    <svg viewBox='0 0 16 16' aria-hidden='true'>
      <circle cx='8' cy='8' r='8' fill='currentColor' />
    </svg>
    <h3>{children}</h3>
  </div>
)

const Card = ({item, isActive}) => {
  // item.title carries a <br/> line-break marker for the heading; the alt text
  // needs it flattened back to a space.
  const plainTitle = (item.title || '').replace(/<br\s*\/?>/g, ' ')

  return (
    <div
      className={`PartnerAws__usecase__card${isActive ? ' is-active' : ''}`}
      aria-hidden={!isActive}
    >
      <div className='PartnerAws__usecase__illustration'>
        {!!item.image && <img src={item.image} alt={plainTitle} />}
      </div>

      <div className='PartnerAws__usecase__content'>
        <div className='PartnerAws__usecase__title-row'>
          <h3 className='PartnerAws__usecase__title' {...html(item.title)} />
          {!!(item.stats || []).length && (
            <div className='PartnerAws__usecase__stat'>
              {item.stats.map((stat, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <div className='PartnerAws__usecase__stat__divider' />
                  )}
                  <div className='PartnerAws__usecase__stat__item'>
                    <div className='PartnerAws__usecase__stat__value'>
                      {stat.value}
                    </div>
                    <p className='PartnerAws__usecase__stat__label'>
                      {stat.label}
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className='PartnerAws__usecase__objective'>
          <SubTopic>{item.objectiveTitle}</SubTopic>
          <p
            className='PartnerAws__usecase__detail-description'
            {...html(item.objectiveDescription)}
          />
        </div>

        <div className='PartnerAws__usecase__details'>
          <div className='PartnerAws__usecase__detail'>
            <SubTopic>{item.solutionTitle}</SubTopic>
            <p className='PartnerAws__usecase__detail-description'>
              {item.solutionDescription}
            </p>
          </div>
          <div className='PartnerAws__usecase__detail'>
            <SubTopic>{item.resultTitle}</SubTopic>
            <p
              className='PartnerAws__usecase__detail-description'
              {...html(item.resultDescription)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function UseCase() {
  const items = zipWithImage(
    t('pages.partnerAws.useCase.items'),
    useCaseIllustrations
  )
  const [currentIndex, currentIndexSet] = React.useState(0)

  return (
    <section className='PartnerAws__usecase'>
      <div className='PartnerAws__inner PartnerAws__usecase__header'>
        <p className='PartnerAws__caption PartnerAws__gradient-text'>
          {t('pages.partnerAws.useCase.caption')}
        </p>
        <h2 className='PartnerAws__section-title'>
          {t('pages.partnerAws.useCase.title')}
        </h2>
        <p className='PartnerAws__usecase__description'>
          {t('pages.partnerAws.useCase.description')}
        </p>
      </div>

      <div className='PartnerAws__inner PartnerAws__usecase__carousel'>
        <div className='PartnerAws__usecase__dots'>
          {items.map((_, index) => (
            <button
              className={`PartnerAws__usecase__dot${
                index === currentIndex ? ' is-active' : ''
              }`}
              key={index}
              type='button'
              aria-label={`Go to use case ${index + 1}`}
              onClick={() => currentIndexSet(index)}
            />
          ))}
        </div>

        {/*
          Every card is rendered into the same grid cell so the container is
          always as tall as the tallest one; only the active card is visible.
          Keeps the height fixed as you page between them instead of the box
          resizing to whichever card is showing.
        */}
        <div className='PartnerAws__usecase__cards'>
          {items.map((item, index) => (
            <Card key={index} item={item} isActive={index === currentIndex} />
          ))}
        </div>

        <div className='PartnerAws__usecase__nav'>
          <button
            className='PartnerAws__usecase__nav__button'
            type='button'
            aria-label='Previous use case'
            disabled={currentIndex === 0}
            onClick={() => currentIndexSet(Math.max(currentIndex - 1, 0))}
          >
            <svg viewBox='0 0 24 24' aria-hidden='true'>
              <path
                d='M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z'
                fill='currentColor'
              />
            </svg>
          </button>
          <button
            className='PartnerAws__usecase__nav__button PartnerAws__usecase__nav__button--active'
            type='button'
            aria-label='Next use case'
            disabled={currentIndex === items.length - 1}
            onClick={() =>
              currentIndexSet(Math.min(currentIndex + 1, items.length - 1))
            }
          >
            <svg viewBox='0 0 24 24' aria-hidden='true'>
              <path
                d='M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z'
                fill='currentColor'
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  )
}
